// src/pages/products/ProductListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Eye,
  Copy,
  PackageX,
  PackageCheck,
  Tag,
  FileText,
  Archive,
  AlertCircle,
  Upload,
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import { getProducts, deleteProduct, updateProductStatus, duplicateProduct } from '../../services/api/products';
import { getProductCategories } from '../../services/api/categories';
import { getUserWarehouses } from '../../services/api/packingBatch';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import { formatDate, formatTime } from '../../utils/dateUtils';
import BundleBadge from '../../components/ui/BundleBadge/BundleBadge';
import styles from './ProductListPage.module.css';

const ProductListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();

  // State management
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 25, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'desc'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    visibility: 'public',
    stockStatus: '',
    categoryId: '',
    warehouseId: ''
  });

  // Bulk actions
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Helper function to get price information from prices array
  const getPriceInfo = (product) => {
    let prices = [];
    
    if (product.prices && Array.isArray(product.prices) && product.prices.length > 0) {
      prices = product.prices.filter(price => price != null && !isNaN(price));
    } else if (product.buyingPrice != null && !isNaN(product.buyingPrice)) {
      prices = [product.buyingPrice];
    }
    
    if (prices.length === 0) {
      return {
        hasPrice: false,
        minPrice: 0,
        maxPrice: 0,
        priceRange: null,
        displayPrice: t('products.noPrice')
      };
    }
    
    const numericPrices = prices.map(p => parseFloat(p));
    const minPrice = Math.min(...numericPrices);
    const maxPrice = Math.max(...numericPrices);
    
    return {
      hasPrice: true,
      minPrice,
      maxPrice,
      priceRange: minPrice === maxPrice ? null : { min: minPrice, max: maxPrice },
      displayPrice: minPrice === maxPrice 
        ? `${minPrice.toFixed(2)}` 
        : `${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}`,
      priceCount: prices.length
    };
  };

  // Get stock quantity respecting selected warehouse
  const getProductStock = (product) => {
    if (filters.warehouseId) {
      // When a specific warehouse is selected, use warehouseStock from backend
      return product.warehouseStock ?? product.stockQuantity ?? 0;
    }
    return product.stockQuantity ?? 0;
  };

  // Load categories and warehouses on mount
  useEffect(() => {
    fetchCategories();
    fetchWarehouses();
  }, []);

  // Load products when filters or pagination/sorting change
  useEffect(() => {
    fetchProducts();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      // Add filters
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.type) params.type = filters.type;
      if (filters.visibility) params.visibility = filters.visibility;
      if (filters.stockStatus) params.stockStatus = filters.stockStatus;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;

      const response = await getProducts(params);
      setProducts(response.products);
      setPagination(prev => ({
        ...prev,
        total: response.total
      }));

    } catch (err) {
      console.error('Failed to fetch products:', err);
      if (err.response?.status === 403) {
        error(t('errors.permissionDenied'));
      } else {
        error(t('products.failedToLoadData'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getProductCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await getUserWarehouses();
      setWarehouses(response.warehouses || []);
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
    }
  };

  const handleFilterChange = (newFilters) => {
    // Reset to page 1 when warehouse changes (stock totals change)
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: '',
      visibility: 'public',
      stockStatus: '',
      categoryId: '',
      warehouseId: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.entries(filters).some(([key, value]) =>
      key === 'visibility' ? value !== 'public' : value !== ''
    );
  };

  const handleProductStatusToggle = async (productId, currentVisibility, productName) => {
    const newVisibility = currentVisibility === 'public' ? 'draft' : 'public';
    const actionText = newVisibility === 'public' ? t('products.public').toLowerCase() : t('products.draft').toLowerCase();

    const confirmed = await confirm(
      t('products.confirmStatusChange', { action: actionText, productName }),
      {
        title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} ${t('nav.products')}`,
        confirmText: actionText.charAt(0).toUpperCase() + actionText.slice(1),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await updateProductStatus(productId, newVisibility);
      setProducts(prev =>
        prev.map(product =>
          product.id === productId
            ? { ...product, visibility: newVisibility }
            : product
        )
      );
      success(
        t('products.statusUpdateSuccess', {
          productName,
          status: newVisibility === 'public' ? t('products.public').toLowerCase() : t('products.draft').toLowerCase()
        }),
        { duration: 3000 }
      );
    } catch (err) {
      console.error('Failed to update product status:', err);
      error(t('products.statusUpdateFailed'));
    }
  };

  const handleDuplicateProduct = async (productId, productName) => {
    const confirmed = await confirm(
      t('products.confirmDuplicate', { productName }),
      {
        title: t('products.duplicateProduct'),
        confirmText: t('common.duplicate'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      const response = await duplicateProduct(productId);
      success(t('products.duplicateSuccess', { productName: response.product.name }));
      fetchProducts();
    } catch (err) {
      console.error('Failed to duplicate product:', err);
      error(t('products.duplicateFailed'));
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    const confirmed = await confirm(
      t('products.confirmDelete', { productName }),
      {
        title: t('products.deleteProduct'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteProduct(productId);
      success(t('products.deleteSuccess', { productName }));
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      error(t('products.deleteFailed'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;

    const confirmed = await confirm(
      t('products.confirmBulkDelete', { count: selectedProducts.length }),
      {
        title: t('products.bulkDelete'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await Promise.all(selectedProducts.map(id => deleteProduct(id)));
      success(t('products.bulkDeleteSuccess', { count: selectedProducts.length }));
      setSelectedProducts([]);
      fetchProducts();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      error(t('products.bulkDeleteFailed'));
    }
  };

  const getSelectedWarehouseName = () => {
    if (!filters.warehouseId) return t('products.allWarehouses') || 'All Warehouses';
    const wh = warehouses.find(w => w.id.toString() === filters.warehouseId.toString());
    return wh ? wh.name : 'All Warehouses';
  };

  const handleExport = () => {
    const warehouseLabel = getSelectedWarehouseName();

    const headers = [
      t('products.productName'),
      t('products.productTypeShort'),
      t('products.categoriesShort'),
      t('products.price'),
      `${t('products.stock')} (${warehouseLabel})`,
      t('common.status'),
      t('common.created')
    ];
    
    const rows = products.map(product => {
      const priceInfo = getPriceInfo(product);
      const totalStock = getProductStock(product);
      
      return [
        product.name,
        product.type === 'variable'
          ? t('products.variableProductShort')
          : product.type === 'bundle' ? t('products.bundleProductShort') : t('products.simpleProductShort'),
        product.productCategories?.map(c => c.name).join('; ') || t('products.noCategories'),
        priceInfo.displayPrice,
        totalStock,
        product.visibility === 'public' ? t('products.public') : t('products.draft'),
        formatDate(product.createdAt)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStockStatusBadge = (product) => {
    const totalStock = getProductStock(product);

    if (totalStock === 0) {
      return { status: 'outOfStock', label: t('products.outOfStock'), className: styles.outOfStock };
    } else if (totalStock <= 10) {
      return { status: 'lowStock', label: t('products.lowStock'), className: styles.lowStock };
    } else {
      return { status: 'inStock', label: t('products.active'), className: styles.inStock };
    }
  };

  const getVisibilityBadge = (visibility) => {
    switch (visibility) {
      case 'public':
        return { label: t('products.public'), className: styles.public };
      case 'draft':
        return { label: t('products.draft'), className: styles.draft };
      case 'trash':
        return { label: t('products.trash'), className: styles.trash };
      default:
        return { label: visibility, className: styles.draft };
    }
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('products.warehouse') || 'Warehouse',
      type: 'select',
      placeholder: t('products.allWarehouses') || 'All Warehouses',
      options: warehouses.map(wh => ({
        value: wh.id.toString(),
        label: wh.name
      }))
    },
    {
      key: 'type',
      label: t('products.productTypeShort'),
      type: 'select',
      placeholder: t('products.allTypes'),
      options: [
        { value: 'simple', label: t('products.simpleProductShort') },
        { value: 'variable', label: t('products.variableProductShort') },
        { value: 'bundle', label: t('products.bundleProductShort') }
      ]
    },
    {
      key: 'visibility',
      label: t('products.visibility'),
      type: 'select',
      placeholder: t('products.allVisibility'),
      options: [
        { value: 'public', label: t('products.public') },
        { value: 'draft', label: t('products.draft') },
        { value: 'trash', label: t('products.trash') }
      ]
    },
    {
      key: 'stockStatus',
      label: t('common.status'),
      type: 'select',
      placeholder: t('products.allStatus'),
      options: [
        { value: 'inStock', label: t('products.inStock') },
        { value: 'lowStock', label: t('products.lowStock') },
        { value: 'outOfStock', label: t('products.outOfStock') }
      ]
    },
    {
      key: 'categoryId',
      label: t('products.categoriesShort'),
      type: 'select',
      placeholder: t('products.allCategories'),
      options: categories.map(category => ({
        value: category.id.toString(),
        label: category.name
      }))
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'product',
      header: t('products.productDetails'),
      sortable: true,
      sortKey: 'name',
      width: '30%',
      render: (row) => (
        <div className={styles.productInfo}>
          <div className={styles.productImage}>
            {row.mainImage ? (
              <img
                src={getImageUrl(row.mainImage)}
                alt={row.name}
                className={styles.productImg}
                onError={(e) => handleImageError(e)}
              />
            ) : (
              <div className={styles.imagePlaceholder}>
                <Package size={20} />
              </div>
            )}
          </div>
          <div className={styles.productDetails}>
            <span className={styles.productName}>{row.name}</span>
            {row.sku && (
              <div className={styles.productMeta}>
                <Tag size={12} />
                <span title={t('products.sku')}>{row.sku}</span>
              </div>
            )}
            {row.barcode && (
              <div className={styles.productMeta}>
                <FileText size={12} />
                <span title={t('products.barcode')}>{row.barcode}</span>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'type',
      header: t('products.productTypeShort'),
      width: '12%',
      render: (row) => row.type === 'bundle' ? (
        <BundleBadge variant="list" />
      ) : (
        <div className={styles.typeBadge}>
          {row.type === 'variable' ? (
            <>
              <PackageX size={14} />
              <span>{t('products.variableProductShort')}</span>
            </>
          ) : (
            <>
              <PackageCheck size={14} />
              <span>{t('products.simpleProductShort')}</span>
            </>
          )}
        </div>
      )
    },
    {
      key: 'categories',
      header: t('products.categoriesShort'),
      width: '15%',
      render: (row) => (
        row.productCategories && row.productCategories.length > 0 ? (
          <div className={styles.categoriesList}>
            {row.productCategories.slice(0, 2).map(category => (
              <span key={category.id} className={styles.categoryTag}>
                {category.name}
              </span>
            ))}
            {row.productCategories.length > 2 && (
              <span className={styles.moreCategoriesIndicator}>
                +{row.productCategories.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span className={styles.noCategories}>{t('products.noCategories')}</span>
        )
      )
    },
    {
      key: 'price',
      header: t('products.price'),
      width: '12%',
      align: 'right',
      render: (row) => {
        const priceInfo = getPriceInfo(row);
        return (
          <div className={styles.priceInfo}>
            <div className={styles.priceContent}>
              {priceInfo.hasPrice ? (
                <>
                  {priceInfo.priceCount === 1 && (
                    <span className={styles.price}>
                      {priceInfo.displayPrice}
                    </span>
                  )}
                  {priceInfo.priceCount > 1 && (
                    <div className={styles.priceMetadata}>
                      {priceInfo.priceRange && (
                        <div className={styles.priceRange}>
                          <span className={styles.priceRangeValue}>
                            {priceInfo.priceRange.min.toFixed(2)} - {priceInfo.priceRange.max.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <span className={styles.noPrice}>{priceInfo.displayPrice}</span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'stock',
      header: filters.warehouseId
        ? `${t('products.stock')} (${getSelectedWarehouseName()})`
        : t('products.stock'),
      sortable: true,
      sortKey: 'stockQuantity',
      width: '10%',
      align: 'center',
      render: (row) => {
        const stockStatus = getStockStatusBadge(row);
        const totalStock = getProductStock(row);
        
        return (
          <div className={`${styles.stockBadge} ${stockStatus.className}`}>
            {stockStatus.status === 'outOfStock' && <PackageX size={14} />}
            {stockStatus.status === 'lowStock' && <AlertCircle size={14} />}
            {stockStatus.status === 'inStock' && <PackageCheck size={14} />}
            <span>{totalStock}</span>
          </div>
        );
      }
    },
    {
      key: 'visibility',
      header: t('common.status'),
      width: '10%',
      render: (row) => {
        const visibilityBadge = getVisibilityBadge(row.visibility);
        return (
          <div className={`${styles.statusBadge} ${visibilityBadge.className}`}>
            <span>{visibilityBadge.label}</span>
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      sortable: true,
      width: '11%',
      render: (row) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}>
            {formatDate(row.createdAt)}
          </div>
          <div className={styles.timeInfo}>
            {formatTime(row.createdAt)}
          </div>
        </div>
      )
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        {hasPermission('view_all_products') && (
          <Link
            to={`/products/${row.id}`}
            className={`${styles.actionButton} ${styles.viewButton}`}
            title={t('products.viewDetails')}
            onClick={(e) => e.stopPropagation()}
          >
            <Eye size={14} />
          </Link>
        )}

        {hasPermission('edit_product') && (
          <Link
            to={`/products/${row.id}/edit`}
            className={`${styles.actionButton} ${styles.editButton}`}
            title={t('products.editProduct')}
            onClick={(e) => e.stopPropagation()}
          >
            <Edit size={14} />
          </Link>
        )}
      </div>
    ),
    dropdown: (row) => {
      const items = [];

      if (hasPermission('edit_product')) {
        items.push({
          label: row.visibility === 'public' ? t('products.makeDraft') : t('products.makePublic'),
          icon: row.visibility === 'public' ? <Archive size={14} /> : <PackageCheck size={14} />,
          onClick: () => handleProductStatusToggle(row.id, row.visibility, row.name)
        });
        items.push({
          label: t('products.duplicateProduct'),
          icon: <Copy size={14} />,
          onClick: () => handleDuplicateProduct(row.id, row.name)
        });
      }

      if (hasPermission('delete_product')) {
        items.push({
          label: t('common.delete'),
          icon: <Trash2 size={14} />,
          onClick: () => handleDeleteProduct(row.id, row.name),
          danger: true
        });
      }

      return items;
    },
    width: '120px',
    frozen: true
  };

  return (
    <div className={styles.productListPage}>
      {/* Page Header */}
      <PageHeader
        title={t('products.allProducts')}
        description={t('products.manageProductInventory')}
        icon={Package}
        actions={
          hasPermission('create_product') && (
            <>
              <Link to="/products/bulk-import" className={styles.secondaryButton}>
                <Upload size={18} />
                {t('products.bulkImportProducts')}
              </Link>
              <Link to="/products/create" className={styles.primaryButton}>
                <Plus size={20} />
                {t('products.addProduct')}
              </Link>
            </>
          )
        }
      />

      {/* Bulk Actions Bar */}
      {selectedProducts.length > 0 && hasPermission('delete_product') && (
        <div className={styles.bulkActionsBar}>
          <span className={styles.bulkActionsText}>
            {t('products.productsSelected', { count: selectedProducts.length })}
          </span>
          <button
            onClick={handleBulkDelete}
            className={styles.bulkActionButton}
          >
            <Trash2 size={18} />
            {t('products.deleteSelected', { count: selectedProducts.length })}
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('products.searchProducts')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={products}
        columns={columns}
        actions={tableActions}
        selectable={hasPermission('delete_product')}
        selectedRows={selectedProducts}
        onSelectionChange={setSelectedProducts}
        onRowClick={(row) => navigate(`/products/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() 
            ? t('products.noProductsFound')
            : t('products.noProductsYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('products.noProductsFoundDesc')
            : t('products.noProductsYetDesc')
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_product') ? (
            <Link to="/products/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('products.addFirstProduct')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default ProductListPage;